import { Directive, Input, OnInit } from '@angular/core';
import { LoadingController, ModalController, PopoverController } from '@ionic/angular';
import _ from 'lodash';

import { delay } from 'src/app/utils/utils';
import { SlackChannel } from 'src/models';
import { MessageService } from 'src/services/message.service';
import { SlackService } from 'src/services/slack.service';

import { ActionDropdownComponent, ActionDropdownType } from '../../action-dropdown/action-dropdown.component';

@Directive()
export abstract class BaseChannelSelectorModal implements OnInit {
  // Shared Inputs
  @Input() defaultInternalOnly = false;
  @Input() defaultExternalOnly = false;
  @Input() hideChannelTypeFilter = false;
  @Input() hideSecondaryTeams = true;

  // Shared State Properties
  public channels: SlackChannel[] = [];
  public connectedTeams: any[];
  public selectedTeam: any;
  public filteredChannels: SlackChannel[] = [];
  public userConnectedToSlack = false;
  public teamConnectedToSlack = false;
  public isLoading: boolean;
  public isLoadingChannels: boolean;
  public isOpen = true; // Used for polling loop in connectUserToSlack
  public isConnecting = false;
  public searchText: string;
  public filterType: 'all' | 'shared' | 'internal' = 'all';
  public publicChannelsCursor: string | null = null;
  public connectedChannelsCursor: string | null = null;
  public userChannelsCursor: string | null = null;

  constructor(
    protected modalCtrl: ModalController,
    protected msgService: MessageService,
    protected slackService: SlackService,
    protected popoverCtrl: PopoverController,
    protected loadingCtrl: LoadingController,
  ) {}

  // Shared ngOnInit logic (with adjustments for selected state initialization)
  async ngOnInit() {
    this.initializeSelectionState(); // Let derived class handle its specific state
    this.initializeFilterType();

    this.isLoading = true;

    try {
      this.connectedTeams = await this.slackService.getConnectedTeams();
      if (this.connectedTeams && this.connectedTeams.length > 0) {
        this.selectedTeam = this.connectedTeams[0];
        await this.loadChannels();
      } else {
        console.warn('No Slack teams connected.');
        this.channels = [];
        this.filteredChannels = [];
        this.teamConnectedToSlack = false; // Ensure this is false if no teams
      }
    } catch (error) {
      console.error('Error loading connected teams:', error);
      this.msgService.showError('Failed to load Slack connection details.');
      this.channels = [];
      this.filteredChannels = [];
      this.teamConnectedToSlack = false;
    } finally {
      this.isLoading = false;
    }
  }

  // Abstract method for derived classes to set up their specific selected state
  abstract initializeSelectionState(): void;

  // Moved filter type initialization logic here
  protected initializeFilterType(): void {
    if (this.defaultExternalOnly) {
      this.filterType = 'shared';
    } else if (this.defaultInternalOnly) {
      // Let derived class check its specific selection state if needed
      if (this.shouldDefaultForceInternalFilter()) {
        this.filterType = 'internal';
      }
    }
  }

  // Abstract method for derived classes to influence initial internal filtering
  abstract shouldDefaultForceInternalFilter(): boolean;

  // Shared loadChannels method
  async loadChannels(clear = false) {
    if (!this.selectedTeam?.connectionId) {
      console.warn('Attempted to load channels without a selected team connection ID.');
      this.channels = [];
      this.filteredChannels = [];
      this.isLoadingChannels = false;
      return;
    }
    if (clear) {
      this.channels = [];
      this.connectedChannelsCursor = null;
      this.publicChannelsCursor = null;
      this.userChannelsCursor = null;
    }

    this.isLoadingChannels = true;
    try {
      const [teamScopes, userScopes] = await Promise.all([
        this.slackService.getScopes(this.selectedTeam.connectionId),
        this.slackService.getUserScopes(this.selectedTeam.connectionId),
      ]);

      this.teamConnectedToSlack = teamScopes.exists;
      this.userConnectedToSlack =
        userScopes.exists && !(await this.slackService.requiresUserScope(undefined, this.selectedTeam.connectionId));

      if (this.teamConnectedToSlack) {
        if (this.userConnectedToSlack) {
          const promises = [];

          if (this.userChannelsCursor !== '') {
            promises.push(
              this.slackService.getChannelsForUser(
                true,
                this.selectedTeam.connectionId,
                this.selectedTeam.id,
                this.userChannelsCursor,
              ),
            );
          } else {
            promises.push(Promise.resolve({ channels: [], cursor: '' }));
          }

          const [{ channels: userChannels, cursor: userChannelsCursor }] = await Promise.all(promises);

          this.channels = _.uniqBy(this.channels.concat(userChannels), 'id');
          this.userChannelsCursor = userChannelsCursor;
        } else {
          const promises = [];

          if (this.connectedChannelsCursor !== '') {
            promises.push(
              this.slackService.getConnectedChannels(
                true,
                this.selectedTeam.connectionId,
                this.selectedTeam.id,
                this.connectedChannelsCursor,
              ),
            );
          } else {
            promises.push(Promise.resolve({ channels: [], cursor: '' }));
          }

          if (this.publicChannelsCursor !== '' && teamScopes.scopes['channels:join']) {
            promises.push(
              this.slackService.getPublicChannels(
                true,
                this.selectedTeam.connectionId,
                this.selectedTeam.id,
                this.publicChannelsCursor,
              ),
            );
          } else {
            promises.push(Promise.resolve({ channels: [], cursor: '' }));
          }

          const [
            { channels: connectedChannels, cursor: connectedChannelsCursor },
            { channels: publicChannels, cursor: publicChannelsCursor },
          ] = await Promise.all(promises);

          this.channels = _.uniqBy(this.channels.concat(_.unionBy(connectedChannels, publicChannels, 'id')), 'id');
          this.connectedChannelsCursor = connectedChannelsCursor;
          this.publicChannelsCursor = publicChannelsCursor;
        }

        this.handleFilters(); // Apply filters after loading
      } else {
        this.channels = []; // Ensure channels are cleared if team isn't connected
        this.filteredChannels = [];
      }
    } catch (error) {
      console.error('Error loading Slack channels:', error);
      this.msgService.showError('Failed to load Slack channels.');
      this.channels = [];
      this.filteredChannels = [];
    } finally {
      this.isLoadingChannels = false;
    }
  }

  // Shared handleFilters method
  handleFilters() {
    const term = this.searchText?.toLowerCase();

    if (!this.channels) {
      this.filteredChannels = [];
      return;
    }

    this.filteredChannels = this.channels.filter((channel) => {
      let passSearch = !term || channel.name.toLowerCase().includes(term);
      let passFilter = true; // Default to true, then apply specific filters

      if (this.filterType === 'shared') {
        passFilter = channel.isShared;
      } else if (this.filterType === 'internal') {
        passFilter = !channel.isShared;
      }

      return passSearch && passFilter;
    });
  }

  // Shared changeFilter method
  async changeFilter() {
    const options: ActionDropdownType[] = [
      {
        label: 'All',
        icon: this.filterType === 'all' ? 'checkmark' : '', // Use empty string for no icon
      },
      {
        label: 'Internal only',
        icon: this.filterType === 'internal' ? 'checkmark' : '',
      },
      {
        label: 'Shared only',
        icon: this.filterType === 'shared' ? 'checkmark' : '',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (idx: number) => {
          popover.dismiss();

          const newFilterType = idx === 0 ? 'all' : idx === 1 ? 'internal' : 'shared';
          if (this.filterType !== newFilterType) {
            this.filterType = newFilterType;
            this.handleFilters();
          }
        },
      },
      showBackdrop: false,
      // Consider adding event propagation stop if needed
    });
    popover.present();
  }

  // Shared Slack connection methods
  async connectUserToSlack() {
    if (this.isConnecting) {
      return;
    }
    this.isConnecting = true;
    this.isLoading = true; // Show loading indicator

    this.slackService.userOauth(
      `${window.location.origin}/close-window`,
      window.location.origin,
      !this.connectedTeams?.find((team) => team.connectionId === this.selectedTeam?.connectionId)?.isPrimary,
    );

    await delay(5000); // Initial delay

    // Polling loop
    while (this.isOpen && !this.userConnectedToSlack) {
      try {
        await this.loadChannels(); // Re-check connection status and load channels
      } catch (error) {
        console.error('Error during user connection polling:', error);
        // Avoid infinite loops on persistent errors
        await delay(10000);
      }
      if (!this.userConnectedToSlack) {
        await delay(2000);
      }
    }

    this.isConnecting = false;
    if (this.isOpen) {
      // Only set isLoading to false if modal is still open
      this.isLoading = false;
    }
  }

  connectTeamToSlack() {
    this.slackService.oauth(
      window.location.href, // Redirect back here
      window.location.origin,
      !(this.connectedTeams?.find((team) => team.connectionId === this.selectedTeam?.connectionId)?.isPrimary ?? true),
    );
  }

  // Shared cancel method (basic dismiss)
  cancel() {
    this.isOpen = false; // Stop polling loop if active
    this.modalCtrl.dismiss();
  }

  // Abstract methods for derived classes
  abstract isChannelSelected(channel: SlackChannel): boolean;
  abstract handleChannelInteraction(channel: SlackChannel): void;
  abstract confirmSelection(): void; // Even single select needs a way to confirm/dismiss
}
